// The part of a group cost an owner reads when other owners own units in the
// same group. The API then marks the cost `viewerPart` and keeps only the
// payment splits of the viewer's own tenants (see Invoice.viewerPart).
import { Invoice } from "./types";

// Whether the cost was settled right away instead of being left to the
// settlement: its payments were asked from the tenants, or it was marked paid
// together with the split it was paid by. A cost whose payments were all
// withdrawn has neither any more and returns to the settlement.
const isSettledRightAway = (invoice: Invoice): boolean =>
  !!invoice.paymentRequestedAt || (!!invoice.paidAt && !!invoice.distribution);

// A group cost settled right away that an owner reads as their own part only,
// while none of their own tenants was charged a share of it: their units stood
// empty in the cost's period, or carried no weight in its split. There is no
// tenant payment of theirs to follow, and the other owners' tenants are not
// theirs to see, so neither "not split yet" nor the building's whole amount
// applies to them.
export const isSettledWithoutOwnTenants = (invoice: Invoice): boolean =>
  !!invoice.viewerPart &&
  invoice.target !== "owner" &&
  (invoice.paymentSplits ?? []).length === 0 &&
  isSettledRightAway(invoice);

// Whether the cost detail treats a cost as a group's. The page tells it from
// the property the cost is on, but the owner of one unit in a group with other
// owners cannot read the group itself. The API marks such a cost
// `viewerPart`, which only a group cost can be.
export const isGroupCostDetail = (params: {
  invoice: Invoice;
  propertyType?: "unit" | "building";
}): boolean =>
  params.propertyType === "building" || !!params.invoice.viewerPart;

// Whether the cost detail shows only what the owner's own tenants were billed:
// their own tenants' splits, or that none of them was charged. No split over
// the whole building is redone for them: only their own units are known, and
// a split over those alone would put the building's whole amount on them.
export const showsOwnPartOnly = (invoice: Invoice): boolean =>
  !!invoice.viewerPart &&
  ((invoice.paymentSplits ?? []).length > 0 ||
    isSettledWithoutOwnTenants(invoice));
